/** @jsx onetext.jsx.element */

import type { OneText } from '../../../global';

// eslint-disable-next-line no-restricted-syntax
import { BrandBackgroundDefault as BrandBackground, BrandLogoDefault as BrandLogo } from '../components';
// eslint-disable-next-line no-restricted-syntax
import { defaultBrandConfig, SUPPRESSED_ROUTES } from '../config';
// eslint-disable-next-line no-restricted-syntax
import { SnackCupGraphic as RewardGraphic } from '../images';

import type { GetPopupAPIResponse, Milliseconds } from '@onetext/api';
import { BUTTON_LEVEL, BUTTON_SIZE, POPUP_LAYOUT, SPINNER_ANIMATION } from '@onetext/api';

declare let onetext : OneText;

const {
    clsx,
    useState
} = onetext.jsx;

const {
    Popup,
    Page,
    EmailField,
    PhoneField,
    Button,
    Row,
    Columns,
    Column,
    Quiz,
    QuizOption,
    Spinner,
    Terms,
    Teaser,
    PopupContainer
} = onetext.component.consent.popup;

type SpinnerPopupProps = {
    popup : GetPopupAPIResponse,
};

export const SpinnerPopup = ({
    popup
} : SpinnerPopupProps) : JSX.Element | null => {
    const [ isFinalSpin, setIsFinalSpin ] = useState(false);

    return (
        <PopupContainer
            popup={ { ...popup, config: defaultBrandConfig } }
            suppressedRoutes={ SUPPRESSED_ROUTES }>
            <Popup
                layout={ POPUP_LAYOUT.FULLSCREEN }
                background={ <BrandBackground /> }>
                <Page openKeyboardOnSubmit>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column className={ 'pb-0 md:pb-14' }>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-4xl md:text-5xl',
                                        'uppercase tracking-wider',
                                        'text-center font-semibold mb-10'
                                    )
                                }>
                                    { 'Spin to Win' }
                                </p>
                                <div className={ 'w-[264px] flex flex-col items-center gap-2.5' }>
                                    <Button
                                        size={ BUTTON_SIZE.LARGE }>
                                        { 'Try my luck' }
                                    </Button>
                                    <Button
                                        level={ BUTTON_LEVEL.SECONDARY }
                                        size={ BUTTON_SIZE.SMALL }
                                        closePopup>
                                        { 'No, thanks' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ 'col-span-1 hidden md:flex' }>
                                <Spinner animation={ SPINNER_ANIMATION.SPIN_TEASE } />
                            </Column>
                        </Columns>
                    </Row>
                    <div
                        className={
                            clsx(
                                'block md:hidden',
                                'absolute bottom-[-140px]',
                                'left-1/2 transform -translate-x-1/2'
                            )
                        }
                    >
                        <Spinner animation={ SPINNER_ANIMATION.SPIN_TEASE } />
                    </div>
                </Page>

                <Page openKeyboardOnSubmit>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column className={ 'pb-0 md:pb-14' }>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-4xl md:text-5xl',
                                        'uppercase tracking-wider',
                                        'text-center font-semibold'
                                    )
                                }>
                                    { 'Spin to Win' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-sm md:text-md',
                                        'font-light leading-relaxed',
                                        'text-center mt-3 mb-6',
                                        'w-[280px]'
                                    )
                                }>
                                    { 'To claim your discount, tell us the age you\'re shopping for today:' }
                                </p>
                                <Quiz propertyName={ 'shopping_age' }>
                                    <div className={ 'w-[264px] flex flex-col items-center gap-2.5' }>
                                        <QuizOption propertyValue={ 'newborn' }>
                                            <Button>
                                                { 'Newborn' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '6m_24m' }>
                                            <Button>
                                                { '6M - 24M' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '2t_4t' }>
                                            <Button>
                                                { '2T - 4T' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '5_years_plus' }>
                                            <Button>
                                                { '5 YEARS+' }
                                            </Button>
                                        </QuizOption>
                                    </div>
                                </Quiz>
                            </Column>
                            <Column className={ 'col-span-1 hidden md:flex' }>
                                <Spinner animation={ SPINNER_ANIMATION.SPINNING } />
                            </Column>
                        </Columns>
                    </Row>
                    <div
                        className={
                            clsx(
                                'block md:hidden',
                                'absolute bottom-[-140px]',
                                'left-1/2',
                                'transform -translate-x-1/2'
                            )
                        }
                    >
                        <Spinner animation={ SPINNER_ANIMATION.SPINNING } />
                    </div>
                </Page>

                <Page openKeyboardOnSubmit>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column className={ 'pb-0 md:pb-14' }>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-4xl md:text-5xl',
                                        'uppercase tracking-wider',
                                        'text-center font-semibold'
                                    )
                                }>
                                    { 'Spin to Win' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-sm md:text-md',
                                        'font-light leading-relaxed',
                                        'text-center mt-3 mb-6',
                                        'w-[280px]'
                                    )
                                }>
                                    { 'Enter your email to stop the wheel' }
                                </p>
                                <div className={ 'w-[264px] flex flex-col items-center gap-3.5' }>
                                    <EmailField autofocus={ true } />
                                    <Button>
                                        { 'Continue' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ 'col-span-1 hidden md:flex' }>
                                <Spinner animation={ SPINNER_ANIMATION.SPINNING } />
                            </Column>
                        </Columns>
                    </Row>
                    <div
                        className={
                            clsx(
                                'block md:hidden',
                                'absolute bottom-[-140px]',
                                'left-1/2',
                                'transform -translate-x-1/2'
                            )
                        }
                    >
                        <Spinner animation={ SPINNER_ANIMATION.SPINNING } />
                    </div>
                </Page>

                <Page
                    submitDelay={ 5000 as Milliseconds }
                    onSubmitStart={ () => setIsFinalSpin(true) }
                >
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column className={ 'pb-0 md:pb-14' }>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-4xl md:text-[40px]',
                                        'font-semibold uppercase',
                                        'tracking-wider text-center',
                                        'md:whitespace-nowrap'
                                    )
                                }>
                                    { 'Almost there!' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-sm md:text-md',
                                        'font-light',
                                        'leading-relaxed',
                                        'text-center',
                                        'mt-3 mb-6',
                                        'w-[280px]',
                                        'md:whitespace-nowrap'
                                    )
                                }>
                                    { 'Finish signing up with text to stop the wheel' }
                                </p>
                                <Terms
                                    brandName={ 'Caden Lane' }
                                    termsUrl={ 'https://cadenlane.com/Pages/terms-of-service' }
                                    privacyUrl={ 'https://cadenlane.com/Pages/privacy-policy' }
                                />
                                <div className={ 'w-[264px] flex flex-col items-center gap-3.5 mt-8' }>
                                    <PhoneField autofocus={ true } />
                                    <Button>
                                        { 'Stop the wheel' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ 'col-span-1 hidden md:flex' }>
                                <Spinner animation={
                                    isFinalSpin
                                        ? SPINNER_ANIMATION.SPIN_FINAL
                                        : SPINNER_ANIMATION.SPINNING
                                } />
                            </Column>
                        </Columns>
                    </Row>
                    <div
                        className={
                            clsx(
                                'block md:hidden',
                                'absolute bottom-[-140px]',
                                'left-1/2 transform -translate-x-1/2'
                            )
                        }
                    >
                        <Spinner animation={ SPINNER_ANIMATION.SPINNING } />
                    </div>
                </Page>

                <Page>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column className={ 'pb-0 md:pb-14' }>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-2xl font-semibold',
                                        'uppercase tracking-wider',
                                        'text-center'
                                    )
                                }>
                                    { 'You\'ve got a' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-4xl md:text-5xl',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-semibold',
                                        'md:whitespace-nowrap',
                                        'mt-2 mb-3'
                                    )
                                }>
                                    { 'Free snack cup' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-sm md:text-xl',
                                        'font-light italic',
                                        'uppercase tracking-wider',
                                        'text-center',
                                        'mb-2 md:mb-4'
                                    )
                                }>
                                    { 'With your first purchase of $75+' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-sm md:text-md',
                                        'font-normal md:font-light',
                                        'leading-relaxed',
                                        'text-center',
                                        'mt-3 mb-6',
                                        'w-[280px]'
                                    )
                                }>
                                    { 'Your snack cup is in your cart.' }<br />
                                    { 'Use this code at checkout:' }
                                </p>
                                <div className={
                                    clsx(
                                        'flex flex-col items-center justify-center',
                                        'bg-[rgba(255,255,255,0.34)]',
                                        'border-4 border-dashed border-black',
                                        'text-[#1C1D1D] fira-sans text-base',
                                        'h-16 px-5 py-[18px]',
                                        'relative z-[1]',
                                        'mb-10'
                                    )
                                }>
                                    <div className={
                                        clsx(
                                            'text-black fira-sans',
                                            'text-xl font-extrabold',
                                            'tracking-[1px] leading-5',
                                            'text-center'
                                        )
                                    }>
                                        { 'HELLOSNACK' }
                                    </div>
                                </div>
                                <div className={ 'w-[264px] flex flex-col items-center gap-2.5' }>
                                    <Button>
                                        { 'Shop now' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={
                                clsx(
                                    'col-span-1 hidden md:flex',
                                    'flex-col items-center justify-center'
                                )
                            }>
                                <img src={ RewardGraphic } />
                                <div className={
                                    clsx(
                                        'flex flex-col items-center justify-center',
                                        'bg-[rgba(242,238,232,0.97)]',
                                        'border border-black rounded-[2px]',
                                        'px-4 py-1',
                                        'tracking-[0.4px] mt-2'
                                    )
                                }>
                                    <div className={
                                        clsx(
                                            'text-black twentieth-century',
                                            'text-base font-semibold',
                                            'tracking-[7px]',
                                            'text-center uppercase'
                                        )
                                    }>
                                        { 'Exclusive Color!' }
                                    </div>
                                </div>
                            </Column>
                        </Columns>
                    </Row>
                    <div className={
                        clsx(
                            'flex flex-col items-center justify-center',
                            'md:hidden',
                            'absolute inset-x-0 bottom-0',
                            'max-h-[32%]'
                        )
                    }>
                        <img
                            className={ 'z-[4] w-[320px] max-w-[100%]' }
                            src={ RewardGraphic } />
                        <div className={
                            clsx(
                                'absolute left-1/2 bottom-[30px]',
                                '-translate-x-1/2 md:mt-2 z-[5]',
                                'flex flex-col items-center justify-center',
                                'bg-[rgba(242,238,232,0.97)]',
                                'border border-black rounded-[2px]',
                                'px-4 py-1',
                                'tracking-[0.4px]',
                                'backdrop-blur-sm'
                            )
                        }>
                            <div className={
                                clsx(
                                    'twentieth-century whitespace-nowrap',
                                    'text-black text-sm font-semibold',
                                    'tracking-[7px]',
                                    'text-center uppercase'
                                )
                            }>
                                { 'Exclusive Color!' }
                            </div>
                        </div>
                    </div>
                </Page>
            </Popup>
            <Teaser customText={ 'Get 15% off' } />
        </PopupContainer>
    );
};
